import axios from "axios"

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? ""
const AUTH_TOKEN_STORAGE_KEY = "eptw.jwt"
const AUTH_SESSION_STORAGE_KEY = "eptw.auth.session"

export type AuthSessionInfo = {
  authProvider?: string | null
  userType?: string | null
  loginMethod?: "LOCAL" | "AZURE" | null
}

export const buildUrl = (path: string) => {
  if (!API_BASE_URL) return path
  return `${API_BASE_URL.replace(/\/$/, "")}${path}`
}

export const clearAuthToken = () => {
  try {
    localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY)
  } catch {
    // ignore storage failures
  }
  try {
    sessionStorage.removeItem(AUTH_SESSION_STORAGE_KEY)
  } catch {
    // ignore storage failures
  }
  delete axios.defaults.headers.common.Authorization
}

export const setAuthSessionInfo = (value: AuthSessionInfo) => {
  try {
    sessionStorage.setItem(AUTH_SESSION_STORAGE_KEY, JSON.stringify(value))
  } catch {
    // ignore storage failures
  }
}

export const getAuthSessionInfo = (): AuthSessionInfo | null => {
  try {
    const raw = sessionStorage.getItem(AUTH_SESSION_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as AuthSessionInfo
    return parsed && typeof parsed === "object" ? parsed : null
  } catch {
    return null
  }
}

export const setAuthToken = (token?: string | null) => {
  if (token && token.trim().length > 0) {
    axios.defaults.headers.common.Authorization = `Bearer ${token}`
    try {
      localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token)
    } catch {
      // ignore storage failures
    }
    return
  }
  clearAuthToken()
}

export const applyStoredToken = () => {
  try {
    const stored = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY)
    if (stored) {
      axios.defaults.headers.common.Authorization = `Bearer ${stored}`
      return stored
    }
  } catch {
    // ignore storage failures
  }
  return null
}

export const getStoredAuthToken = () => {
  try {
    return localStorage.getItem(AUTH_TOKEN_STORAGE_KEY)
  } catch {
    return null
  }
}

export interface LoginRequestDto {
  username: string
  password: string
  userType?: string
}

export interface AzureLoginRequestDto {
  idToken: string
  userType?: string
}

export interface ContractorAzureResolveRequestDto {
  idToken: string
}

export interface ContractorAzureOnboardingSubmitRequestDto {
  idToken: string
  username: string
  fullName?: string
  position?: string
  phone?: string
  remark?: string
  tenantId?: string
  companyId: string
  controllerUserId: string
}

export interface ContractorAzureOnboardingStateDto {
  status: "NOT_REGISTERED" | "WAITING" | "ACTIVE" | string
  userId?: string | null
  username?: string | null
  fullName?: string | null
  email?: string | null
  position?: string | null
  phone?: string | null
  remark?: string | null
  tenantId?: string | null
  companyId?: string | null
  companyName?: string | null
  controllerUserId?: string | null
  controllerName?: string | null
  isActive?: boolean
  createdAt?: string | null
  message?: string | null
}

export interface LoginRoleDto {
  roleId: string
  code?: string | null
  displayName?: string | null
  departmentId?: string | null
  isDelegated?: boolean
  delegatorUserId?: string | null
}

export interface LoginMenuDto {
  menuId: string
  tenantId: string
  code: string
  label: string
  parentId?: string | null
  routePath?: string | null
  externalUrl?: string | null
  icon?: string | null
  sortOrder: number
  isActive: boolean
}

export interface LoginTenantDto {
  tenantId: string
  tenantName?: string | null
  roles: LoginRoleDto[]
  menus: LoginMenuDto[]
}

export interface LoginResponseDto {
  userId: string
  username: string
  fullName?: string | null
  companyId?: string | null
  companyD?: string | null
  email?: string | null
  isActive: boolean
  endDate?: string | null
  authProvider: string
  tenants: LoginTenantDto[]
  token?: string | null
}

export interface ContractorPasswordResetRequest {
  email: string
}

export interface ContractorPasswordResetConfirm {
  token: string
  otp: string
  newPassword: string
  confirmPassword?: string
}

export interface ContractorPasswordResetResponse {
  message?: string | null
}

export async function login(request: LoginRequestDto): Promise<LoginResponseDto> {
  const response = await axios.post<LoginResponseDto>(buildUrl("/api/v1/auth/login"), request)
  const data = response.data ?? ({} as LoginResponseDto)
  const token = (data as any).token ?? (data as any).Token ?? null
  if (!data?.userId || !token) {
    throw new Error("Invalid login response")
  }
  setAuthToken(token)
  setAuthSessionInfo({
    authProvider: data.authProvider ?? null,
    userType: request.userType?.trim().toUpperCase() ?? null,
    loginMethod: "LOCAL",
  })
  return { ...data, token }
}

export async function loginWithAzureToken(request: AzureLoginRequestDto): Promise<LoginResponseDto> {
  const response = await axios.post<LoginResponseDto>(buildUrl("/api/v1/auth/azure/login"), request)
  const data = response.data ?? ({} as LoginResponseDto)
  const token = (data as any).token ?? (data as any).Token ?? null
  if (!data?.userId || !token) {
    throw new Error("Invalid Azure login response")
  }
  setAuthToken(token)
  setAuthSessionInfo({
    authProvider: data.authProvider ?? null,
    userType: request.userType?.trim().toUpperCase() ?? null,
    loginMethod: "AZURE",
  })
  return { ...data, token }
}

export async function resolveContractorAzureOnboarding(
  request: ContractorAzureResolveRequestDto,
): Promise<ContractorAzureOnboardingStateDto> {
  const response = await axios.post<ContractorAzureOnboardingStateDto>(
    buildUrl("/api/v1/auth/azure/contractor/onboarding/resolve"),
    request,
  )
  return response.data ?? { status: "NOT_REGISTERED" }
}

export async function submitContractorAzureOnboarding(
  request: ContractorAzureOnboardingSubmitRequestDto,
): Promise<ContractorAzureOnboardingStateDto> {
  const response = await axios.post<ContractorAzureOnboardingStateDto>(
    buildUrl("/api/v1/auth/azure/contractor/onboarding/submit"),
    request,
  )
  return response.data ?? { status: "WAITING" }
}

export async function cancelContractorAzureOnboarding(
  request: ContractorAzureResolveRequestDto,
): Promise<ContractorAzureOnboardingStateDto> {
  const response = await axios.post<ContractorAzureOnboardingStateDto>(
    buildUrl("/api/v1/auth/azure/contractor/onboarding/cancel"),
    request,
  )
  return response.data ?? { status: "NOT_REGISTERED" }
}

export async function requestContractorPasswordReset(
  payload: ContractorPasswordResetRequest,
): Promise<ContractorPasswordResetResponse> {
  const response = await axios.post<ContractorPasswordResetResponse>(
    buildUrl("/api/v1/auth/contractor/password-reset/request"),
    payload,
  )
  return response.data ?? {}
}

export async function confirmContractorPasswordReset(
  payload: ContractorPasswordResetConfirm,
): Promise<ContractorPasswordResetResponse> {
  const response = await axios.post<ContractorPasswordResetResponse>(
    buildUrl("/api/v1/auth/contractor/password-reset/confirm"),
    payload,
  )
  return response.data ?? {}
}
